import React, { useState, useEffect } from 'react';
import { Card, Select, Button, Space, Tag, Spin, Empty, message, Form } from 'antd';
import { SaveOutlined, LinkOutlined } from '@ant-design/icons';
import { PrimeArtifactWorkflowItem, UpdatePrimeArtifact } from '../../types/primeArtifact';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { PrimeArtifact } from '../../types/primeArtifact';
import { useArtifactTypes } from '../../hooks/useArtifactTypes';
import { apiClient } from '../../services/apiClient';
import styles from './ArtifactDetailsPanel.module.css';

// StateTemplate interface (matches backend response)
interface StateTemplate {
  id: string;
  name: string;
  description?: string;
  is_deleted?: boolean;
  created_at?: string;
  updated_at?: string;
}

const { Option } = Select;

interface ArtifactDetailsPanelProps {
  selectedArtifact: PrimeArtifactWorkflowItem | null;
  onArtifactUpdated?: (artifact: PrimeArtifactWorkflowItem) => void;
  onCreateArtifactType?: () => void;
  onCreateGlobalProperty?: () => void;
}

const WORKFLOW_STATUS_COLORS: Record<string, string> = {
  IDENTIFIED: 'default',
  DEFINED: 'processing',
  DESIGNED: 'processing',
  IMPLEMENTED: 'warning',
  VALIDATED: 'success',
};

export const ArtifactDetailsPanel: React.FC<ArtifactDetailsPanelProps> = ({
  selectedArtifact,
  onArtifactUpdated,
  onCreateArtifactType,
  onCreateGlobalProperty,
}) => {
  const [fullArtifact, setFullArtifact] = useState<PrimeArtifact | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { artifactTypes } = useArtifactTypes();
  const [stateTemplates, setStateTemplates] = useState<StateTemplate[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  
  // Edit state
  const [editingArtifactTypeId, setEditingArtifactTypeId] = useState<string | null>(null);
  const [editingStateTemplateId, setEditingStateTemplateId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Load artifact details
  useEffect(() => {
    if (selectedArtifact) {
      setLoading(true);
      PrimeArtifactService.getById(selectedArtifact.id)
        .then((artifact) => {
          setFullArtifact(artifact);
          setEditingArtifactTypeId(artifact.artifact_type_id || null);
          setEditingStateTemplateId(artifact.state_template_id || null);
          setHasChanges(false);
        })
        .catch((error) => {
          console.error('Error loading artifact details:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setFullArtifact(null);
      setEditingArtifactTypeId(null);
      setEditingStateTemplateId(null);
      setHasChanges(false);
    }
  }, [selectedArtifact]);

  // Load state templates (artifact types are now loaded via hook)
  useEffect(() => {
    const loadTemplates = async () => {
      setLoadingOptions(true);
      try {
        const templatesResponse = await apiClient.get('/state-templates');
        setStateTemplates(templatesResponse.data || []);
      } catch (error) {
        console.error('Error loading templates:', error);
        message.error('Failed to load state templates');
      } finally {
        setLoadingOptions(false);
      }
    };
    loadTemplates();
  }, []);

  const handleArtifactTypeChange = (value: string) => {
    setEditingArtifactTypeId(value);
    setHasChanges(true);
  };

  const handleStateTemplateChange = (value: string) => {
    setEditingStateTemplateId(value);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!fullArtifact || !selectedArtifact) return;

    setSaving(true);
    try {
      // Build update data matching PrimeArtifactUpdate schema
      // Only include fields that have changed
      const updateData: Record<string, any> = {};
      
      if (editingArtifactTypeId !== fullArtifact.artifact_type_id) {
        // Ensure we have a valid UUID string
        if (editingArtifactTypeId) {
          updateData.artifact_type_id = editingArtifactTypeId;
        } else {
          message.error('Artifact type is required');
          setSaving(false);
          return;
        }
      }
      
      // Handle state_template_id updates
      // Compare as strings to handle null/undefined properly
      const currentStateTemplateId = fullArtifact.state_template_id || null;
      const newStateTemplateId = editingStateTemplateId || null;
      if (newStateTemplateId !== currentStateTemplateId) {
        // Allow null to remove template reference
        updateData.state_template_id = newStateTemplateId;
      }

      if (Object.keys(updateData).length === 0) {
        message.info('No changes to save');
        setSaving(false);
        return;
      }

      console.log('[ArtifactDetailsPanel] Updating artifact:', {
        artifactId: selectedArtifact.id,
        updateData,
        currentArtifactTypeId: fullArtifact.artifact_type_id,
        newArtifactTypeId: editingArtifactTypeId
      });

      // Use the standard update endpoint (ISSUE-115: backend UUID parameter binding fixed)
      const updated = await PrimeArtifactService.update(selectedArtifact.id, updateData);
      
      // Update the full artifact
      setFullArtifact(updated);
      setHasChanges(false);
      
      // Notify parent to refresh the list
      if (onArtifactUpdated) {
        const updatedArtifactType = artifactTypes.find(t => t.id === editingArtifactTypeId);
        const updatedWorkflowItem: PrimeArtifactWorkflowItem = {
          ...selectedArtifact,
          artifact_type_name: updatedArtifactType?.name || null,
          // Keep the original state_template_name since we can't update it
          state_template_name: selectedArtifact.state_template_name,
        };
        onArtifactUpdated(updatedWorkflowItem);
      }
      
      message.success('Artifact updated successfully');
    } catch (error: any) {
      console.error('Error saving artifact:', error);
      console.error('Error response:', error?.response?.data);
      console.error('Error status:', error?.response?.status);
      
      // Try to extract a meaningful error message
      let errorMessage = 'Failed to save changes';
      if (error?.response?.data) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.detail && typeof error.response.data.detail === 'object') {
          // Handle validation errors
          if (Array.isArray(error.response.data.detail)) {
            errorMessage = error.response.data.detail.map((err: any) => 
              `${err.loc?.join('.')}: ${err.msg || err.message || 'Validation error'}`
            ).join(', ');
          } else {
            errorMessage = JSON.stringify(error.response.data.detail);
          }
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      message.error(`Failed to save: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  if (!selectedArtifact) {
    return (
      <div className={styles.artifactDetailsPanel}>
        <Empty
          description="Select an artifact to view details"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.artifactDetailsPanel}>
        <Spin size="large" />
      </div>
    );
  }

  const selectedArtifactType = artifactTypes.find(t => t.id === editingArtifactTypeId);
  const selectedStateTemplate = stateTemplates.find(t => t.id === editingStateTemplateId);

  return (
    <div className={styles.artifactDetailsPanel}>
      <Card
        title={selectedArtifact.name}
        extra={
          <Tag color={WORKFLOW_STATUS_COLORS[selectedArtifact.development_status]}>
            {selectedArtifact.development_status}
          </Tag>
        }
      >
        <Form layout="vertical">
          <Form.Item label="Artifact Type">
            <Select
              value={editingArtifactTypeId}
              onChange={handleArtifactTypeChange}
              placeholder="Select artifact type"
              loading={loadingOptions}
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
              allowClear
            >
              {artifactTypes.map((type) => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Global Template (State Template)">
            <Select
              value={editingStateTemplateId}
              onChange={handleStateTemplateChange}
              placeholder="Select state template"
              loading={loadingOptions}
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
              allowClear
            >
              {stateTemplates.map((template) => (
                <Option key={template.id} value={template.id}>
                  {template.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {hasChanges && (
            <Form.Item>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
                block
              >
                Save Changes
              </Button>
            </Form.Item>
          )}
        </Form>

        <div className={styles.artifactDetailsActions} style={{ marginTop: '16px' }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {onCreateArtifactType && (
              <Button
                type="default"
                icon={<LinkOutlined />}
                onClick={onCreateArtifactType}
                block
              >
                Create Artifact Type
              </Button>
            )}
            {onCreateGlobalProperty && (
              <Button
                type="default"
                icon={<LinkOutlined />}
                onClick={onCreateGlobalProperty}
                block
              >
                Create Global Property
              </Button>
            )}
          </Space>
        </div>
      </Card>
    </div>
  );
};
